import { useEffect, useSyncExternalStore } from "react";
import { QuantityUnit, SettingName } from "../models/setting";
import { apiService } from "../services/api.service";

/**
 * Unità di misura delle quantità, letta dalle impostazioni una volta per sessione.
 *
 * Sta fuori da React perché la usa anche `formatQuantity`, che è una funzione semplice
 * chiamata ovunque: così ogni quantità a video porta la sua unità senza che ogni pagina debba
 * leggerla. Chi la cambia dalle impostazioni la aggiorna qui, e la shell ridisegna le pagine.
 */
const labels: Record<QuantityUnit, string> = {
    [QuantityUnit.KG]: 'kg',
    [QuantityUnit.QT]: 'q',
    [QuantityUnit.TN]: 't',
};

let current: QuantityUnit | undefined;
let loading: Promise<void> | undefined;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
};

/** Sigla dell'unità, es. "t"; vuota finché le impostazioni non sono arrivate. */
export const quantityUnitLabel = () => current ? labels[current] : '';

export function setQuantityUnit(unit: QuantityUnit) {
    current = unit;
    listeners.forEach(listener => listener());
}

/** Legge l'impostazione; senza, vale il chilogrammo come sul server. */
export function loadQuantityUnit() {
    return loading ??= apiService.GetSettingByName(SettingName.QuantityUnit)
        .then(response => {
            const value = response.data?.value;
            setQuantityUnit(value && value in QuantityUnit ? value as QuantityUnit : QuantityUnit.KG);
        })
        .catch(() => {
            // Un errore non resta memorizzato: si riprova alla prossima richiesta.
            loading = undefined;
            setQuantityUnit(QuantityUnit.KG);
        });
}

/** Sigla dell'unità, aggiornata quando arriva o cambia. Undefined finché non è nota. */
export function useQuantityUnitLabel() {
    useEffect(() => { loadQuantityUnit(); }, []);
    const label = useSyncExternalStore(subscribe, quantityUnitLabel);
    return label || undefined;
}
